"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.checkConnectedMesh = checkConnectedMesh;
exports.extrudePolygon = extrudePolygon;
exports.getConnectedMeshFromFaces = getConnectedMeshFromFaces;
var _vector = require("./vector");
/**
 * Represents a fully connected mesh or polyhedron. Vertices, edges, and faces are all indexed to each other, so that
 * it is easy to traverse along edges or faces.
 */

/**
 * Given an array of polygons with unindexed vertexes, return a ConnectedMesh.
 * @param faces An array of polygons with unindexed vertexes
 * @param tolerance The tolerance to use when determining if two vertexes are the same
 * @returns A ConnectedMesh, where the faces, edges, and vertexes have all been indexed.
 */
function getConnectedMeshFromFaces(faces, tolerance = 1e-6) {
  const facesUnderConstruction = faces.map(t => ({
    vertexes: t.vertexes.map(p => ({
      ...p
    }))
  }));

  // 1. Collect all the vertexes into a single array. Each item in the array should contain the vertex, the index of the
  //    face the vertex belongs to, and the index of the vertex in the face.
  // 2. Sort the vertexes by one of the coordinates, then use plane sweep to find duplicate vertexes.
  // 3. For each group of duplicate vertexes, add the vertex to a list of unique vertexes, and update the vertex index
  //    in all faces that reference that vertex.

  let allVerts = [];
  let bbox = {
    min: {
      x: Infinity,
      y: Infinity,
      z: Infinity
    },
    max: {
      x: -Infinity,
      y: -Infinity,
      z: -Infinity
    }
  };
  for (let i = 0; i < facesUnderConstruction.length; i++) {
    const face = facesUnderConstruction[i];
    for (let j = 0; j < face.vertexes.length; j++) {
      const vertex = face.vertexes[j];
      allVerts.push({
        vertex,
        faceIndex: i,
        vertexIndex: j,
        used: false
      });
      bbox.min = (0, _vector.min_3)(bbox.min, vertex);
      bbox.max = (0, _vector.max_3)(bbox.max, vertex);
    }
  }

  // Which coordinate spans the largest range?
  let maxRange = bbox.max.x - bbox.min.x;
  let sortDimension = 'x';
  if (bbox.max.y - bbox.min.y > maxRange) {
    maxRange = bbox.max.y - bbox.min.y;
    sortDimension = 'y';
  }
  if (bbox.max.z - bbox.min.z > maxRange) {
    maxRange = bbox.max.z - bbox.min.z;
    sortDimension = 'z';
  }

  // Sort the vertexes by the chosen coordinate.
  allVerts.sort((a, b) => a.vertex[sortDimension] - b.vertex[sortDimension]);

  // Use plane sweep to find duplicate vertexes.
  let uniqueVerts = [];
  const tolerance2 = tolerance * tolerance;
  let i = 0;
  while (i < allVerts.length) {
    if (allVerts[i].used) {
      i++;
      continue;
    }

    // Create a new vertex
    let uniqueVertex = {
      vertex: allVerts[i].vertex,
      edgeRefs: [],
      faceRefs: [{
        faceIndex: allVerts[i].faceIndex,
        vertexIndex: allVerts[i].vertexIndex
      }]
    };
    allVerts[i].used = true;

    // Search forward for vertexes that are within tolerance of vertex i, and add them to refs.
    let j = i + 1;
    while (j < allVerts.length && allVerts[j].vertex[sortDimension] - allVerts[i].vertex[sortDimension] < tolerance) {
      if (!allVerts[j].used && (0, _vector.norm2_3)((0, _vector.sub_3)(allVerts[i].vertex, allVerts[j].vertex)) < tolerance2) {
        uniqueVertex.faceRefs.push({
          faceIndex: allVerts[j].faceIndex,
          vertexIndex: allVerts[j].vertexIndex
        });
        allVerts[j].used = true;
      }
      j++;
    }

    // No need to search backwards, since we already processed all the vertexes before i.
    uniqueVerts.push({
      ...uniqueVertex
    });
    i++;
  }

  // Restore original vertex order
  uniqueVerts.sort((a, b) => a.faceRefs[0].faceIndex - b.faceRefs[0].faceIndex || a.faceRefs[0].vertexIndex - b.faceRefs[0].vertexIndex);

  // Now that we have a list of unique vertexes, we can update the triangle indexes.
  for (let i = 0; i < uniqueVerts.length; i++) {
    const uniqueVertex = uniqueVerts[i];
    for (let j = 0; j < uniqueVertex.faceRefs.length; j++) {
      const ref = uniqueVertex.faceRefs[j];
      facesUnderConstruction[ref.faceIndex].vertexes[ref.vertexIndex].index = i;
    }
  }

  // Remove any faces that have duplicate vertexes
  for (let i = facesUnderConstruction.length - 1; i >= 0; i--) {
    const face = facesUnderConstruction[i];
    let hasDuplicateVertexes = false;
    for (let j = 0; j < face.vertexes.length - 1 && !hasDuplicateVertexes; j++) {
      for (let k = j + 1; k < face.vertexes.length; k++) {
        if (face.vertexes[j].index === face.vertexes[k].index) {
          hasDuplicateVertexes = true;
          break;
        }
      }
      if (hasDuplicateVertexes) {
        // Remove the face from the list
        facesUnderConstruction.splice(i, 1);

        // Fix indices of uniqueVerts[].vertexes.faceRefs[].faceIndex
        for (let k = 0; k < uniqueVerts.length; k++) {
          const uniqueVertex = uniqueVerts[k];
          for (let l = uniqueVertex.faceRefs.length - 1; l >= 0; l--) {
            if (uniqueVertex.faceRefs[l].faceIndex === i) {
              // Remove the face from the list
              uniqueVertex.faceRefs.splice(l, 1);
            } else if (uniqueVertex.faceRefs[l].faceIndex > i) {
              // Update the face index
              uniqueVertex.faceRefs[l].faceIndex--;
            }
          }
        }
        break;
      }
    }
  }

  // Now add edges.
  let edges = [];

  // Loop over each unique vertex
  for (let i = 0; i < uniqueVerts.length; i++) {
    const firstVertex = uniqueVerts[i];
    const addedNeighbors = [];
    const addedEdges = [];
    // Loop over each face that touches this vertex
    for (let j = 0; j < firstVertex.faceRefs.length; j++) {
      const face = facesUnderConstruction[firstVertex.faceRefs[j].faceIndex];

      // Get index of this vertex in the face
      const thisIndex = firstVertex.faceRefs[j].vertexIndex;
      let adjacentIndexes = [(thisIndex - 1 + face.vertexes.length) % face.vertexes.length, (thisIndex + 1) % face.vertexes.length];

      // Loop over adjacent vertexes in the face
      for (let k of adjacentIndexes) {
        // To avoid duplicates, we'll only add edges where the first vertex is less than the second vertex. Is the index
        // of the vertex in the face less than the index of the current vertex? In other words, they are not the same
        // vertex, and we haven't already added an edge for this pair of vertexes.
        if (i < face.vertexes[k].index) {
          if (!addedNeighbors.includes(face.vertexes[k].index)) {
            let newEdge = {
              v: [i, face.vertexes[k].index],
              f: []
            };
            addedEdges.push(newEdge);
            addedNeighbors.push(face.vertexes[k].index);
          }

          // Add the face to the edge
          let addedEdgesIndex = addedNeighbors.indexOf(face.vertexes[k].index);
          addedEdges[addedEdgesIndex].f.push(firstVertex.faceRefs[j].faceIndex);
        }
      }
    }
    edges.push(...addedEdges);
  }

  // Add edge indices to vertexes
  for (let i = 0; i < edges.length; i++) {
    const edge = edges[i];
    uniqueVerts[edge.v[0]].edgeRefs.push(i);
    uniqueVerts[edge.v[1]].edgeRefs.push(i);
  }
  const indexedFaces = facesUnderConstruction.map(t => ({
    v: t.vertexes.map(v => v.index),
    e: []
  }));

  // Add edge indexes to faces
  for (let i = 0; i < edges.length; i++) {
    const edge = edges[i];
    for (let j = 0; j < edge.f.length; j++) {
      const faceIndex = edge.f[j];
      indexedFaces[faceIndex].e.push(i);
    }
  }
  return {
    type: "polyhedron",
    verts: uniqueVerts.map(v => ({
      p: {
        x: v.vertex.x,
        y: v.vertex.y,
        z: v.vertex.z
      },
      e: v.edgeRefs,
      f: v.faceRefs.map(f => f.faceIndex)
    })),
    edges,
    faces: indexedFaces
  };
}

/**
 * Extrudes a polygon and returns a connected mesh of the result. This is faster than generating the convex hull from just the points.
 * @param poly The polygon to extrude.
 * @param height The height to extrude.
 * @returns A connected mesh of the extruded polygon.
 */
function extrudePolygon(poly, height) {
  let n = poly.length;
  let verts = []; // length 2n
  let edges = []; // length 3n
  let faces = []; // length n+2
  faces[n] = {
    v: [],
    e: []
  };
  faces[n + 1] = {
    v: [],
    e: []
  };
  for (let i = 0; i < n; i++) {
    let im1 = (i + n - 1) % n;
    let ip1 = (i + 1) % n;
    verts[i] = {
      p: {
        ...poly[i],
        z: 0
      },
      e: [im1, i, 2 * n + i],
      f: [im1, i, n]
    };
    verts[n + i] = {
      p: {
        ...poly[i],
        z: height
      },
      e: [n + im1, n + i, 2 * n + i],
      f: [im1, i, n + 1]
    };
    edges[i] = {
      v: [i, ip1],
      f: [i, n]
    };
    edges[n + i] = {
      v: [n + i, n + ip1],
      f: [i, n + 1]
    };
    edges[2 * n + i] = {
      v: [i, n + i],
      f: [im1, i]
    };
    faces[i] = {
      v: [i, ip1, n + ip1, n + i],
      e: [i, 2 * n + ip1, n + i, 2 * n + i]
    };
    faces[n].v[i] = i;
    faces[n].e[i] = i;
    faces[n + 1].v[i] = n + i;
    faces[n + 1].e[i] = n + i;
  }
  return {
    type: "polyhedron",
    verts,
    edges,
    faces
  };
}

/**
 * Checks that a connected mesh is manifold and indexed correctly.
 * @param mesh The mesh to check.
 * @param allowHoles If true, the mesh is allowed to have boundary edges (holes).
 * @returns True if the mesh is connected correctly, throws an error otherwise.
 */
function checkConnectedMesh(mesh, allowHoles = false) {
  let verts = mesh.verts;
  let edges = mesh.edges;
  let faces = mesh.faces;

  // Check that each vertex has the correct number of edges
  for (let i = 0; i < verts.length; i++) {
    let v = verts[i];
    if (!allowHoles && v.e.length !== v.f.length) {
      throw new Error(`Vertex ${i} has ${v.e.length} edges but ${v.f.length} faces. (Did you forget allowHoles?)`);
    }
    if (allowHoles && v.e.length > v.f.length + 1) {
      throw new Error(`Vertex ${i} has ${v.e.length} edges but ${v.f.length} faces.`);
    }
  }

  // Check that each edge has the correct number of vertices and faces
  for (let i = 0; i < edges.length; i++) {
    let e = edges[i];
    if (e.v.length !== 2) {
      throw new Error(`Edge ${i} has ${e.v.length} vertices, it must have exactly two.`);
    }
    if (!allowHoles && e.f.length !== 2) {
      throw new Error(`Edge ${i} has ${e.f.length} faces, it must have exactly two. (Did you forget allowHoles?)`);
    }
    if (allowHoles && (e.f.length < 1 || e.f.length > 2)) {
      throw new Error(`Edge ${i} has ${e.f.length} faces, it must have either one or two.`);
    }
  }

  // Check that each face has the correct number of vertices and edges
  for (let i = 0; i < faces.length; i++) {
    let f = faces[i];
    if (f.v.length !== f.e.length) {
      throw new Error(`Face ${i} has ${f.v.length} vertices but ${f.e.length} edges.`);
    }
  }

  // Check that each vertex has the correct edges and faces
  for (let i = 0; i < verts.length; i++) {
    let v = verts[i];
    for (let j = 0; j < v.e.length; j++) {
      let e = edges[v.e[j]];
      if (!e.v.includes(i)) {
        throw new Error(`Vertex ${i} has edge ${v.e[j]} but edge ${v.e[j]} does not have vertex ${i}.`);
      }
    }
    for (let j = 0; j < v.f.length; j++) {
      let f = faces[v.f[j]];
      if (!f.v.includes(i)) {
        throw new Error(`Vertex ${i} has face ${v.f[j]} but face ${v.f[j]} does not have vertex ${i}.`);
      }
    }
  }

  // Check that each edge has the correct vertices and
  // faces
  for (let i = 0; i < edges.length; i++) {
    let e = edges[i];
    for (let j = 0; j < e.v.length; j++) {
      let v = verts[e.v[j]];
      if (!v.e.includes(i)) {
        throw new Error(`Edge ${i} has vertex ${e.v[j]} but vertex ${e.v[j]} does not have edge ${i}.`);
      }
      for (let k = 0; k < v.f.length; k++) {
        if (!v.f.includes(v.f[k])) {
          throw new Error(`Edge ${i} has face ${v.f[k]} but vertex ${e.v[j]} does not have face ${v.f[k]}.`);
        }
      }
    }
    for (let j = 0; j < e.f.length; j++) {
      let f = faces[e.f[j]];
      if (!f.e.includes(i)) {
        throw new Error(`Edge ${i} has face ${e.f[j]} but face ${e.f[j]} does not have edge ${i}.`);
      }
    }
  }

  // Check that each face has the correct vertices and
  // edges
  for (let i = 0; i < faces.length; i++) {
    let f = faces[i];
    for (let j = 0; j < f.v.length; j++) {
      let v = verts[f.v[j]];
      if (!v.f.includes(i)) {
        throw new Error(`Face ${i} has vertex ${f.v[j]} but vertex ${f.v[j]} does not have face ${i}.`);
      }
    }
    for (let j = 0; j < f.e.length; j++) {
      let e = edges[f.e[j]];
      if (!e.f.includes(i)) {
        throw new Error(`Face ${i} has edge ${f.e[j]} but edge ${f.e[j]} does not have face ${i}.`);
      }
    }
  }
  return true;
}